// Floating damage-number sprites on hit.
// Gate 1: simple canvas-text sprites rendered in the Silkscreen UI font.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';
import { formatInt } from '../ui/format.js';
import { easeOutCubic } from './easing.js';

const MAX_NUMBERS = 8; // concurrent cap: AoE fire merges or drops, never floods
const LIFETIME = 0.75;
const RISE_SPEED = 1.6;
// A hit landing within MERGE_RADIUS of a live number's hit point, within
// MERGE_WINDOW_S of its spawn, merges into it instead of stacking a sprite.
const MERGE_RADIUS = 0.5;
const MERGE_WINDOW_S = 0.18;

interface ActiveNumber {
  sprite: THREE.Sprite;
  life: number;
  age: number;
  value: number;
  hitX: number;
  hitY: number;
  crit: boolean;
  vx: number;
  vy: number;
}

export class DamageNumbers {
  private readonly active: ActiveNumber[] = [];

  constructor(private readonly scene: THREE.Scene) {}

  spawn(pos: Vec2, value: number, crit: boolean): void {
    const v = Math.floor(value);

    // Merge by proximity: a hit close enough to a live number, soon enough after
    // it spawned, sums into it and re-renders its canvas rather than stacking a
    // second sprite on top (the '300300' read). Measured on the stored hit point,
    // not the sprite position — the sprite sits 0.4 u above it, already outside
    // the merge radius.
    for (const n of this.active) {
      if (n.crit !== crit || n.age > MERGE_WINDOW_S) continue;
      const dx = n.hitX - pos.x;
      const dy = n.hitY - pos.y;
      if (dx * dx + dy * dy > MERGE_RADIUS * MERGE_RADIUS) continue;
      this.absorb(n, v);
      return;
    }

    // Focal flood guard: at most ~8 concurrent numbers on screen. Under AoE
    // fire the excess merges above or drops here — the hit flash and the
    // white enemy blink still carry the per-hit read.
    if (this.active.length >= MAX_NUMBERS) return;

    const canvas = this.renderCanvas(formatInt(v), crit);
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      opacity: 1,
    });

    const sprite = new THREE.Sprite(material);
    const worldHeight = this.heightFor(v, crit);
    const worldWidth = worldHeight * (canvas.width / canvas.height);
    sprite.scale.set(worldWidth, worldHeight, 1);
    sprite.position.set(pos.x, pos.y + 0.4, 0.5);
    sprite.userData = { material, lastScale: 1 };

    this.scene.add(sprite);

    const angle = (Math.random() - 0.5) * 0.6;
    const speed = RISE_SPEED * (0.85 + Math.random() * 0.3);
    this.active.push({
      sprite,
      life: LIFETIME,
      age: 0,
      value: v,
      hitX: pos.x,
      hitY: pos.y,
      crit,
      vx: Math.sin(angle) * 0.6,
      vy: Math.cos(angle) * speed,
    });
  }

  private renderCanvas(text: string, crit: boolean): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    // 2× canvas resolution: the sprite's world size is set by worldHeight,
    // not canvas px, so a bigger canvas is pure texture sharpness — at the
    // default 0.5 render scale the 28px digits blurred into blobs.
    const fontSize = crit ? 88 : 56;
    ctx.font = `bold ${fontSize}px Silkscreen, monospace`;
    const metrics = ctx.measureText(text);
    const pad = 16;
    canvas.width = Math.ceil(metrics.width) + pad * 2;
    canvas.height = Math.ceil(fontSize * 1.4) + pad;

    // Opaque dark backing plate: soft glow/outline text drowned against
    // additive kill confetti — the capped few numbers must stay legible at
    // peak kill rate (the banner chip trick, world-space). Crit plates get
    // the gold frame (heavy-hit read); normal hits a quiet ice edge.
    ctx.fillStyle = 'rgba(7, 21, 38, 0.88)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = crit ? '#ffd36b' : 'rgba(159, 239, 255, 0.55)';
    ctx.lineWidth = 4;
    ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2);

    ctx.font = `bold ${fontSize}px Silkscreen, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = crit ? '#fff6a0' : '#ffffff';
    if (crit) {
      // Crits keep a small hot glow over the plate; normal hits stay crisp.
      ctx.shadowColor = '#ffaa00';
      ctx.shadowBlur = 12;
    }
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    return canvas;
  }

  /** Sprite height scales with the hit: chip damage stays small, heavy hits
   * (and merged sums) grow — magnitude reads at a glance. */
  private heightFor(value: number, crit: boolean): number {
    return (crit ? 1.1 : 0.9) * (0.8 + Math.min(0.5, value / 250));
  }

  // Sums a merged hit into a live number and re-renders its canvas in place:
  // swap the texture, keep the sprite and its motion.
  private absorb(n: ActiveNumber, v: number): void {
    n.value += v;
    const canvas = this.renderCanvas(formatInt(n.value), n.crit);
    const texture = new THREE.CanvasTexture(canvas);
    n.sprite.material.map?.dispose();
    n.sprite.material.map = texture;
    // The text may have changed width and the sum may have grown the tier;
    // re-base both axes, keeping the growth factor.
    const worldHeight = this.heightFor(n.value, n.crit);
    const last = n.sprite.userData.lastScale ?? 1;
    n.sprite.scale.x = worldHeight * (canvas.width / canvas.height) * last;
    n.sprite.scale.y = worldHeight * last;
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const n = this.active[i];
      n.life -= dt;
      n.age += dt;
      if (n.life <= 0) {
        this.remove(i);
        continue;
      }
      n.sprite.position.x += n.vx * dt;
      n.sprite.position.y += n.vy * dt;
      const t = n.life / LIFETIME;
      n.sprite.material.opacity = easeOutCubic(Math.min(1, t * 1.6));
      const scale = 1 + easeOutCubic(1 - t) * 0.15;
      n.sprite.scale.x *= scale / (n.sprite.userData.lastScale ?? 1);
      n.sprite.scale.y *= scale / (n.sprite.userData.lastScale ?? 1);
      n.sprite.userData.lastScale = scale;
    }
  }

  dispose(): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      this.remove(i);
    }
  }

  private remove(i: number): void {
    const n = this.active[i];
    this.scene.remove(n.sprite);
    n.sprite.material.map?.dispose();
    n.sprite.material.dispose();
    this.active.splice(i, 1);
  }
}
